import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Axios from 'axios';
import { Auth } from 'aws-amplify';
import Head from 'src/layout/head/Head';
import { API_ROUTES } from 'src/config/api';

import useEditorState from './useEditorState';
import useNodeTypes from './useNodeTypes';
import EditorToolbar from './EditorToolbar';
import NodePalette from './NodePalette';
import EditorCanvas from './EditorCanvas';
import NodeConfigPanel from './NodeConfigPanel';
import PublishModal from './PublishModal';
import ExecutionPanel from './ExecutionPanel';
import { NodeTypeProvider } from './NodeTypeContext';

// Inner editor component — only mounted when playbook data is available
const PlaybookEditorInner = ({ playbook, id, nodeTypes }) => {
  const history = useHistory();
  const { organisation } = useSelector((state) => state);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [navBlockedPath, setNavBlockedPath] = useState(null);
  const unblockRef = useRef(null);

  // Test execution state
  const [testRunning, setTestRunning] = useState(false);
  const [executionResult, setExecutionResult] = useState(null);
  const [executionError, setExecutionError] = useState(null);
  const [executionPanelOpen, setExecutionPanelOpen] = useState(false);
  const pollRef = useRef(null);

  const editorState = useEditorState(playbook, nodeTypes);
  const selectedNode = editorState.draftNodes.find((n) => n.id === selectedNodeId);

  // Unsaved changes guard — browser close/reload
  useEffect(() => {
    const handler = (e) => {
      if (editorState.isDirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [editorState.isDirty]);

  // Unsaved changes guard — in-app navigation
  useEffect(() => {
    if (editorState.isDirty) {
      unblockRef.current = history.block((location) => {
        setNavBlockedPath(location.pathname);
        return false;
      });
    } else if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }
    return () => {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
    };
  }, [editorState.isDirty, history]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        editorState.undo();
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        editorState.redo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [editorState.undo, editorState.redo]);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const handleTestRun = async () => {
    setTestRunning(true);
    setExecutionResult(null);
    setExecutionError(null);
    setExecutionPanelOpen(true);
    if (pollRef.current) clearInterval(pollRef.current);

    try {
      const user = await Auth.currentAuthenticatedUser();
      const headers = { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` };

      const result = await Axios(
        API_ROUTES.playbooks['playbook:test-execute'](id),
        {
          method: 'post',
          data: {
            organisation_id: organisation.organisation,
            nodes: editorState.draftNodes,
            connections: editorState.draftConnections,
          },
          headers,
        }
      );

      const executionId = result.data?.data?.execution_id;
      if (!executionId) {
        setExecutionError('No execution ID returned');
        setTestRunning(false);
        return;
      }

      // Poll for execution results
      let attempts = 0;
      pollRef.current = setInterval(async () => {
        attempts += 1;
        try {
          const execResult = await Axios(
            API_ROUTES.playbooks['playbook:execution'](executionId),
            { method: 'get', headers }
          );
          const data = execResult.data?.data;
          if (data) {
            setExecutionResult(data);
            if (data.status !== 'running') {
              clearInterval(pollRef.current);
              pollRef.current = null;
              setTestRunning(false);
            }
          }
        } catch (pollError) {
          // n8n may not have the execution ready yet, keep polling
          if (attempts > 60) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setExecutionError('Timed out waiting for execution results');
            setTestRunning(false);
          }
        }
      }, 2000);
    } catch (error) {
      const errData = error.response?.data;
      const message = errData?.errors?.[0]?.message
        || errData?.message
        || error.message
        || 'Failed to start test execution';
      setExecutionError(message);
      setTestRunning(false);
    }
  };

  const handlePublish = async (changeDescription) => {
    setPublishing(true);
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Axios(
        API_ROUTES.playbooks['playbook:publish'](id),
        {
          method: 'post',
          data: {
            organisation_id: organisation.organisation,
            nodes: editorState.draftNodes,
            connections: editorState.draftConnections,
            settings: { executionOrder: 'v1' },
            change_description: changeDescription,
          },
          headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
        }
      );
      editorState.resetDirty();
      setPublishModalOpen(false);
      history.push(`/playbooks/playbook/${id}`);
    } catch (error) {
      console.error('Failed to publish:', error);
    } finally {
      setPublishing(false);
    }
  };

  const handleDropNode = useCallback((typeKey, position) => {
    const node = editorState.addNode(typeKey, position);
    setSelectedNodeId(node.id);
  }, [editorState.addNode]);

  const handleDeleteNode = useCallback((nodeId) => {
    setSelectedNodeId((prev) => prev === nodeId ? null : prev);
    editorState.removeNode(nodeId);
  }, [editorState.removeNode]);

  const handleNodeClick = useCallback((nodeId) => {
    setSelectedNodeId((prev) => prev === nodeId ? null : nodeId);
  }, []);

  return (
    <>
      <Head title={`Edit: ${playbook.name}`} />

      {/* Unsaved changes modal */}
      {navBlockedPath && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Unsaved changes
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                You have unpublished changes to this playbook. Are you sure you want to leave? Your changes will be lost.
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-5 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 rounded-b-xl">
              <button
                onClick={() => setNavBlockedPath(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Keep editing
              </button>
              <button
                onClick={() => {
                  if (unblockRef.current) {
                    unblockRef.current();
                    unblockRef.current = null;
                  }
                  history.push(navBlockedPath);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Leave without saving
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
        <EditorToolbar
          playbookId={id}
          playbookName={playbook.name}
          isDirty={editorState.isDirty}
          canUndo={editorState.canUndo}
          canRedo={editorState.canRedo}
          onUndo={editorState.undo}
          onRedo={editorState.redo}
          onAutoLayout={editorState.autoLayout}
          onPublish={() => setPublishModalOpen(true)}
          publishing={publishing}
          onTestRun={handleTestRun}
          testRunning={testRunning}
        />

        <div className="flex-1 flex overflow-hidden">
          <NodePalette nodeTypes={nodeTypes} />

          <div className="flex-1 relative" style={{ height: '100%' }}>
            <EditorCanvas
              nodes={editorState.rfNodes}
              edges={editorState.rfEdges}
              selectedNodeId={selectedNodeId}
              onNodeClick={handleNodeClick}
              onNodeDragStop={editorState.updateNodePosition}
              onConnectHandler={editorState.addConnection}
              onEdgesDeleteHandler={editorState.removeConnection}
              onNodesDeleteHandler={handleDeleteNode}
              onDropNode={handleDropNode}
              onDeleteNode={handleDeleteNode}
            />

            {executionPanelOpen && (
              <ExecutionPanel
                execution={executionResult}
                running={testRunning}
                error={executionError}
                onClose={() => {
                  setExecutionPanelOpen(false);
                  if (pollRef.current) {
                    clearInterval(pollRef.current);
                    pollRef.current = null;
                  }
                  setTestRunning(false);
                }}
              />
            )}
          </div>

          {selectedNode && (
            <NodeConfigPanel
              node={selectedNode}
              onUpdateParameters={editorState.updateNodeParameters}
              onUpdateName={editorState.updateNodeName}
              onDelete={() => handleDeleteNode(selectedNodeId)}
              onClose={() => setSelectedNodeId(null)}
            />
          )}
        </div>

        <PublishModal
          isOpen={publishModalOpen}
          toggle={() => setPublishModalOpen(false)}
          onPublish={handlePublish}
          publishing={publishing}
        />
      </div>
    </>
  );
};

// Outer loader — fetches playbook, only renders editor when ready
const PlaybookEditor = () => {
  const { id } = useParams();
  const { organisation } = useSelector((state) => state);
  const [playbook, setPlaybook] = useState(null);
  const [loading, setLoading] = useState(true);
  const { nodeTypes, loading: nodeTypesLoading } = useNodeTypes();

  useEffect(() => {
    if (!organisation.organisation) return;
    const fetchPlaybook = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const result = await Axios(
          API_ROUTES.playbooks['playbook:get'](id),
          {
            method: 'get',
            headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
            params: { organisation_id: organisation.organisation },
          }
        );
        setPlaybook(result.data.data);
      } catch (error) {
        console.error('Failed to fetch playbook:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPlaybook();
  }, [id, organisation.organisation]);

  if (loading || nodeTypesLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-gray-500">Loading editor...</p>
        </div>
      </div>
    );
  }

  if (!playbook) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-gray-500">Playbook not found</p>
      </div>
    );
  }

  if (nodeTypes.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-gray-500">Failed to load node types</p>
      </div>
    );
  }

  // Only mount the editor once playbook data and node types are available
  return (
    <NodeTypeProvider value={nodeTypes}>
      <PlaybookEditorInner playbook={playbook} id={id} nodeTypes={nodeTypes} />
    </NodeTypeProvider>
  );
};

export default PlaybookEditor;
